import React, { useState, useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";
import RouteTile from "./components/RouteTile";
import RouteMapView from "./components/RouteMapView";
import Card from "components/card";
import DeviceDetails from "./components/DeviceDetails";
import NoData from "../../../assets/img/dashboards/9404370.webp";
import resetIcon from "../../../assets/img/dashboards/reset.png"; 
import "./live.css";
import LiveGauges from "./components/LiveGauges"; 
import StatusBanner from "./components/StatusBanner";
import ReactApexChart from "react-apexcharts";
import { renderLiveChart } from "./liveCharts";
import { readToken } from "utils/session";
import DeviceSelect from "components/DeviceSelect";
import { pointPosition } from "components/RoutePlot";



const getDistanceFromLatLonInKm = (lat1, lon1, lat2, lon2) => {
  const R = 6371; // Radius of the earth in km
  const dLat = (lat2 - lat1) * (Math.PI / 180);
  const dLon = (lon2 - lon1) * (Math.PI / 180);
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(lat1 * (Math.PI / 180)) * Math.cos(lat2 * (Math.PI / 180)) *
    Math.sin(dLon / 2) * Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c; // Distance in km
};


const LIVE_TAIL_LIMIT = 300;

const LiveTracking = () => {
  const [selectedOptions, setSelectedOptions] = useState([]);
  const [devices, setDevices] = useState([]);
  
  const [showPopup, setShowPopup] = useState(false); 
  const [popupMessage, setPopupMessage] = useState(""); 
  const [showReport, setShowReport] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
// --- NEW STATES FOR LIVE T0 DASHBOARD ---
  const [activeTab, setActiveTab] = useState("live"); // 'live' or 'history'
  const [sessionDistance, setSessionDistance] = useState(0.00);
  const lastCoordsRef = useRef(null); // Used to calculate distance between points
  const [liveEvents, setLiveEvents] = useState([]); // Array of max 10 events
  const [liveTelemetry, setLiveTelemetry] = useState([]); // Data for live T0 charts
  const [mapInfo, setMapInfo] = useState([]);
  const [gaugeConfig, setGaugeConfig] = useState([]);
  const [isLiveReceiving, setIsLiveReceiving] = useState(false);
  const [latestSocketData, setLatestSocketData] = useState(null);
  const [deviceDetails, setDeviceDetails] = useState({});
  const [cardSliderInfo, setCardSliderInfo] = useState(null);
  const [reportItems, setReportItems] = useState([]);

  const wsRef = useRef(null);
  const historyCountRef = useRef(0);

  const userKey = readToken();
  const apiBaseUrl = process.env.REACT_APP_API_BASE_URL;
  const location = useLocation();
  const autoLiveRef = useRef("");
  const viewLiveRef = useRef(null);
  const [devicesReady, setDevicesReady] = useState(false);

  // --- NEW: Cleanup WebSocket when leaving the component ---
  useEffect(() => {
    return () => {
      if (wsRef.current) {
        wsRef.current.close();
      }
    };
  }, []);

  useEffect(() => {
    const fetchDevices = async () => {
      try {
        let config = {
          method: "get",
          url: `${apiBaseUrl}/api/fleetlist`,
          headers: { Authorization: `Bearer ${userKey}` },
        };
        const response = await axios.request(config);
        const fleetData = response.data.fleetlist || [];
        setDevices(fleetData.map((device) => ({ aid: device.aid, did: device.did })));
      } catch (error) {
        console.error("Error fetching devices:", error);
      } finally {
        setDevicesReady(true);
      }
    };
    if (userKey) fetchDevices();
    else setDevicesReady(true);
  }, [userKey, apiBaseUrl]);


const fetchAndAddEvent = async (eventId, timestamp) => {
    try {
      const response = await axios.get(`${apiBaseUrl}/api/geteventicon?eventid=${eventId}`, {
        headers: { Authorization: `Bearer ${userKey}` }
      });
      if (response.data && response.data.imgUrl) {
        const newEvent = {
          id: `${eventId}-${Date.now()}`, // Unique key
          icon: response.data.imgUrl.eventImg,
          name: response.data.eventname || `Event Code: ${eventId}`,
          time: timestamp
        };
        // Unshift to top, slice to keep only the 10 most recent
        setLiveEvents(prev => [newEvent, ...prev].slice(0, 10));
      }
    } catch (err) {
      console.error("Error fetching event icon:", err);
    }
  };

  const handleViewLive = async (didArg, aidArg) => {
    const selectedDid = didArg != null && typeof didArg !== "object" ? didArg : selectedOptions[0];
    if (selectedDid == null || selectedDid === "") {
      setPopupMessage("Please select a Device ID to view live.");
      setShowPopup(true);
      setTimeout(() => setShowPopup(false), 3000);
      return;
    }

    // Close any existing WebSocket connection before opening a new one
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }

    setIsLoading(true);
    const selectedDevice = devices.find((device) => String(device.did) === String(selectedDid));
    const aid = (aidArg != null && typeof aidArg !== "object" ? aidArg : "") || selectedDevice?.aid;
    const reportData = { repDid: [selectedDid] };

    let config = {
      method: "get",
      url: `${apiBaseUrl}/api/getassetdetail?reportData=${encodeURIComponent(JSON.stringify(reportData))}`,
      headers: { Authorization: `Bearer ${userKey}` },
    };

    try {
      // 1. Fetch initial historical data & details
      const response = await axios.request(config);
      const data = response.data;

      setDeviceDetails(data.assetDetails || {});
      const initialPoints = data.assetData || [];
      historyCountRef.current = initialPoints.length;
      setMapInfo(initialPoints);
      
      const summaryItems = data.summeryItems?.reportItems || [];
      setReportItems(summaryItems);
      
      const sliderData = summaryItems.find(item => item.type === "cardSlider");
      if(sliderData) setCardSliderInfo(sliderData.cardSliderData);

      setShowReport(true);

      // 2. --- NEW: Initiate Live WebSocket Connection ---
      if (aid) {
        setupWebSocket(aid);
      }

    } catch (error) {
      setShowReport(false);
      setPopupMessage("No live data available for the selected asset.");
      setShowPopup(true);
      setTimeout(() => setShowPopup(false), 3000);
    } finally {
      setIsLoading(false);
    }
  };

  viewLiveRef.current = handleViewLive;

  useEffect(() => {
    const did = location.state?.liveDid;
    if (did == null || did === "" || !userKey) return;
    const fromList = devices.find((device) => String(device.did) === String(did))?.aid;
    const aid = location.state?.liveAid || fromList;
    if (!aid && !devicesReady) return;
    if (autoLiveRef.current === String(did)) return;
    autoLiveRef.current = String(did);
    setSelectedOptions([did]);
    if (!aid) {
      setPopupMessage("Live tracking is not available for this asset.");
      setShowPopup(true);
      setTimeout(() => setShowPopup(false), 3000);
      return;
    }
    viewLiveRef.current(did, aid);
  }, [location.state, devices, devicesReady, userKey]);

  // --- NEW: WebSocket Setup Function ---
  const setupWebSocket = async (aid) => {
    try {
      const wsConfig = {
        method: "get",
        url: `${apiBaseUrl}/api/reqlivetrack?astid=${aid}`,
        headers: { Authorization: `Bearer ${userKey}` },
      };

      const wsResponse = await axios.request(wsConfig);
      
      if (wsResponse.data && wsResponse.data.Result === "ENABLED") {
        const { liveurl, sessionkey, guage_config } = wsResponse.data;
        
        setGaugeConfig(guage_config);
        const sockReqData = JSON.stringify({
          astid: aid,
          wss_session_key: sessionkey
        });
        
        // Encode the payload as a URL parameter
        const socketUrl = `${liveurl}?sockreq=${encodeURIComponent(sockReqData)}`;
        const ws = new WebSocket(socketUrl);

        ws.onmessage = async (event) => {
          try {
            const rawPacket = JSON.parse(event.data);
            

            setIsLiveReceiving(true);
            // --- THE FIX: Map the device schema to the map schema ---
            const livePacket = {
              ...rawPacket, 
              lat: parseFloat(rawPacket.latitude),
              lng: parseFloat(rawPacket.longitude),
              postype: rawPacket.positiontype || rawPacket.postype,
              dirn: parseFloat(rawPacket.direction || 0),
              speed: parseFloat(rawPacket.gspeed || 0),
              timestamp: rawPacket.serverts, // Or devicets, whichever you prefer
              event: rawPacket.event
            };

            setLatestSocketData(livePacket);


            // --- EVENT ICON LOGIC FOR MVP ---
            // Assuming event '0' means normal moving. Only fetch if it's a real alert.
            if (livePacket.event && livePacket.event !== 0) {

            fetchAndAddEvent(livePacket.event, livePacket.timestamp);

            }

            const plotted = pointPosition(livePacket);
            const isGPS = String(livePacket.postype || "").toUpperCase() === "GPS";

            if (plotted) {
               setMapInfo((prev) => {
                 const historyCount = Math.min(historyCountRef.current, prev.length);
                 const history = prev.slice(0, historyCount);
                 const liveTail = prev.slice(historyCount);
                 const nextTail = liveTail.concat(livePacket).slice(-LIVE_TAIL_LIMIT);
                 return history.concat(nextTail);
               });
               
               // Accumulate Live T0 Telemetry (Keep the last 30 points for the chart)
               setLiveTelemetry(prev => [...prev, livePacket].slice(-30));

               // Calculate Distance Odometer
               if (lastCoordsRef.current) {
                 const dist = getDistanceFromLatLonInKm(
                   lastCoordsRef.current.lat, 
                   lastCoordsRef.current.lng, 
                   plotted.lat, 
                   plotted.lng
                 );
                 // Only add if they moved more than ~5 meters (filters out GPS drift/jitter)
                 if (dist > 0.005) {
                   setSessionDistance(prev => prev + dist);
                   lastCoordsRef.current = plotted;
                 }
               } else {
                 // First valid point of the session
                 lastCoordsRef.current = plotted;
               }
            }

              setDeviceDetails(prev => {
              const updated = { ...prev };
              
              // 1. Update the overall Last Update timestamp
              updated.networkts = rawPacket.networkts; 
              
              // 2. Logic for Location Type: GPS(Latest) vs GPS(AsOn: Time)
              if (isGPS && plotted) {
                updated.lastValidGpsTime = rawPacket.gpsts || rawPacket.networkts;
                updated.displayLocationType = "GPS(Latest)";
              } else if (!isGPS && plotted) {
                updated.displayLocationType = "TOWER";
              } else if (updated.lastValidGpsTime) {
                updated.displayLocationType = `GPS(AsOn: ${updated.lastValidGpsTime})`;
              } else if (prev.displayLocationType) {
                updated.displayLocationType = prev.displayLocationType;
              } else {
                const originalRow = prev.deviceDetailsTable?.find(r => r.name.includes("Location Type"));
                updated.displayLocationType = originalRow ? originalRow.value : "TOWER";
              }
              
              return updated;
            });

            
          } catch (err) {
            console.error("❌ Error parsing live packet", err);
          }
        };

        wsRef.current = ws;
      }
    } catch (error) {
      console.error("❌ Failed to negotiate WebSocket connection:", error);
    }
  };

  return (
    <>
      <Card extra="flex flex-col md:flex-row flex-grow items-center rounded-[6px] relative p-5 mt-5 justify-between">
        {/* Dropdown */}
        <DeviceSelect devices={devices} value={selectedOptions} onChange={setSelectedOptions} />

      {/* Action Buttons */}
        <div className="flex flex-col items-center space-y-4 md:flex-row md:space-x-3 md:space-y-0">
          <button
            type="button"
            onClick={() => handleViewLive()}
            disabled={isLoading}
            className="w-full rounded-md bg-blue-500 px-6 py-2 text-white hover:bg-blue-600 md:w-auto disabled:opacity-50"
          >
            {isLoading ? "Fetching..." : "View Live"}
          </button>
          
          {showReport && (
            <button 
              onClick={() => handleViewLive()} 
              className="flex items-center justify-center rounded-md bg-gray-200 p-2 hover:bg-gray-300 dark:bg-navy-800 dark:hover:bg-navy-700"
              title="Refresh Live Data"
            >
               <img src={resetIcon} alt="Refresh" className="h-6 w-6" />
            </button>
          )}
        </div>
      </Card>

      {/* Error Popup */}
      {showPopup && (
        <div className="mt-5 flex justify-end">
          <div className="rounded-md bg-red-500 p-4 text-white shadow-lg">
            {popupMessage}
          </div>
        </div>
      )}

      {/* Main Dashboard Render */}
      {showReport ? (
        <div className="mt-5 grid h-full grid-cols-1 gap-5">
          
          {/* Top Row: Map and Device Details */}
          <div className="grid grid-cols-1 gap-5 lg:grid-cols-4">
            
            {/* Added 'relative' to this wrapper */}
            <div className="lg:col-span-3 relative h-full w-full">
              
              {/* --- NEW: PULSING LIVE INDICATOR --- */}
              {isLiveReceiving && (
                <div className="pointer-events-none absolute right-4 top-4 z-10 flex items-center rounded-md bg-white/90 px-3 py-1.5 shadow-sm backdrop-blur-sm dark:bg-navy-800/90">
                  <span className="relative mr-2 flex h-3 w-3">
                    <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-red-400 opacity-75"></span>
                    <span className="relative inline-flex h-3 w-3 rounded-full bg-red-500"></span>
                  </span>
                  <span className="tracking-wide text-sm font-bold text-red-500">LIVE</span>
                </div>
              )}

              <RouteMapView
                mapInfo={mapInfo}
                isPlaying={false} 
                currentIndex={0}
                selectedEvents={[]} 
                overlayImg={deviceDetails?.AssetMapImg}
              />
            </div>

            <div className="lg:col-span-1">
              <DeviceDetails deviceDetails={deviceDetails} />
            </div>
          </div>

          {/* LIVE GAUGES */}
          {gaugeConfig && gaugeConfig.length > 0 && mapInfo.length > 0 && (
              <LiveGauges 
                configs={gaugeConfig} 
                latestData={latestSocketData || mapInfo[mapInfo.length - 1]} 
              />
          )}

          {/* STATUS BANNER */}
          {mapInfo.length > 0 && (
            <StatusBanner 
              latestData={latestSocketData || mapInfo[mapInfo.length - 1]} 
              sessionDistance={sessionDistance} 
            />
          )}

          {/* --- NEW: TOGGLE TABS --- */}
          <div className="flex w-full mt-2 mb-2 border-b border-gray-200 dark:border-navy-700">
            <button
              className={`pb-3 px-4 text-sm font-bold transition-colors ${activeTab === 'live' ? 'border-b-2 border-blue-500 text-blue-500' : 'text-gray-500 hover:text-gray-700 dark:text-gray-400'}`}
              onClick={() => setActiveTab('live')}
            >
              Live Telemetry (T0)
            </button>
            <button
              className={`pb-3 px-4 text-sm font-bold transition-colors ${activeTab === 'history' ? 'border-b-2 border-blue-500 text-blue-500' : 'text-gray-500 hover:text-gray-700 dark:text-gray-400'}`}
              onClick={() => setActiveTab('history')}
            >
              Historical Trends
            </button>
          </div>

          {/* --- TAB CONTENT --- */}
          {activeTab === 'live' ? (
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              {/* Left Column: Live Event Log */}
              <Card extra="col-span-1 p-4 h-[400px] flex flex-col">
                <h4 className="text-lg font-bold text-navy-700 dark:text-white mb-4">
                  Live Event Log
                </h4>
                <div className="flex-1 overflow-y-auto pr-2 space-y-3">
                  {liveEvents.length > 0 ? (
                    liveEvents.map((ev) => (
                      <div key={ev.id} className="flex items-center p-3 bg-gray-50 dark:bg-navy-800 rounded-lg border border-gray-100 dark:border-navy-700 shadow-sm animate-fadeIn">
                        <img src={ev.icon} alt="event" className="h-8 w-8 object-contain mr-3" />
                        <div className="flex flex-col">
                          <span className="text-xs text-gray-500">{ev.time}</span>
                          <span className="text-sm font-bold text-navy-700 dark:text-white">{ev.name}</span>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="h-full flex items-center justify-center text-gray-400 text-sm italic">
                      Waiting for events...
                    </div>
                  )}
                </div>
              </Card>

            {/* Right Column: Live Multi-Parameter Telemetry */}
              <Card extra="col-span-1 md:col-span-2 p-4 h-[400px]">
                 <h4 className="text-lg font-bold text-navy-700 dark:text-white mb-4">
                  Live Telemetry Profile
                 </h4>
                 <div className="w-full h-full pb-4">
                   {liveTelemetry && liveTelemetry.length > 0 ? (
                     <ReactApexChart 
                       type="line" 
                       height="100%" 
                       options={{
                         chart: { 
                           id: 'live-telemetry',
                           // 🚨 THE FIX: Turn off animations! Fast sockets crash the animation queue.
                           animations: { enabled: false }, 
                           toolbar: { show: false }, 
                           zoom: { enabled: false } 
                         },
                         // 'straight' lines are much safer/faster for live data than 'smooth'
                         stroke: { curve: 'straight', width: 2 }, 
                         colors: ['#3B82F6', '#10B981', '#8B5CF6', '#F59E0B', '#EF4444'], 
                         legend: { 
                           show: true, 
                           position: 'top', 
                           horizontalAlign: 'right',
                           markers: { radius: 12 }
                         },
                         tooltip: {
                           shared: true,
                           intersect: false,
                         },
                         xaxis: { 
                           // 🚨 THE FIX: Tell it to expect categorical X values
                           type: 'category', 
                           labels: { style: { colors: '#A3AED0', fontSize: '10px' } }, 
                           axisBorder: { show: false }, 
                           axisTicks: { show: false } 
                         },
                         yaxis: { 
                           labels: { style: { colors: '#A3AED0' } } 
                         },
                         grid: { show: true, borderColor: '#E2E8F0', strokeDashArray: 4 }
                       }} 
                       series={[
                         { 
                           name: 'Speed', 
                           data: liveTelemetry.map(t => ({ 
                             x: t.timestamp ? t.timestamp.split(' ')[1] : 'T0', 
                             y: Number(t.speed) || 0 
                           })) 
                         },
                         { 
                           name: 'Battery', 
                           data: liveTelemetry.map(t => ({ 
                             x: t.timestamp ? t.timestamp.split(' ')[1] : 'T0', 
                             y: Number(t.batterystatus) || 0 
                           })) 
                         },
                         { 
                           name: 'GSM', 
                           data: liveTelemetry.map(t => ({ 
                             x: t.timestamp ? t.timestamp.split(' ')[1] : 'T0', 
                             y: Number(t.gsmss) || 0 
                           })) 
                         },
                         { 
                           name: 'Satellites', 
                           data: liveTelemetry.map(t => ({ 
                             x: t.timestamp ? t.timestamp.split(' ')[1] : 'T0', 
                             y: Number(t.possatcount) || 0 
                           })) 
                         },
                         { 
                           name: 'HDOP', 
                           data: liveTelemetry.map(t => ({ 
                             x: t.timestamp ? t.timestamp.split(' ')[1] : 'T0', 
                             y: Number(t.hdop) || 0 
                           })) 
                         }
                       ]} 
                     />
                   ) : (
                     <div className="h-full flex items-center justify-center text-gray-400 text-sm italic">
                        Waiting for telemetry data...
                     </div>
                   )}
                 </div>
              </Card>
            </div>

          ) : (
            
            // Historical Tab Content
            <div className="flex flex-col w-full animate-fadeIn">
              {cardSliderInfo && (
                <div className="w-full mb-5">
                  <RouteTile cardSliderInfo={cardSliderInfo} />
                </div>
              )}
              <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
                 {reportItems.filter(item => item.type !== "cardSlider").map((chart, index) => (
                    <Card key={index} extra="p-5 rounded-[20px]">
                       <h4 className="text-lg font-bold text-navy-700 dark:text-white mb-4">
                         {chart.label}
                       </h4>
                       <div className="relative flex h-72 w-full items-center justify-center">
                          {renderLiveChart(chart)}
                       </div>
                       
                    </Card>
                 ))}
              </div>
            </div>

            
          )}
          
        </div>
      ) : (
        <div className="initialMessage flex flex-col items-center justify-center text-center mt-20">
          <img src={NoData} alt="noData" className="h-40" />
          <h4 className="mt-4 text-xl font-bold text-navy-700 dark:text-white">
            Awaiting Asset Selection
          </h4>
          <p className="mt-2 font-dm text-sm font-medium text-gray-600">
            Select a Device ID from the dropdown and click "View Live" to monitor the asset.
          </p>
        </div>
      )}
    </>
    
  );
};

export default LiveTracking;